import { createReducer, on } from '@ngrx/store';
import { StoreInfo } from '../../core/models';
import { loadSession } from '../../core/utils/session.util';
import { AuthActions } from './auth.actions';

export type AuthStatus = 'guest' | 'verifying' | 'authenticated';

export interface AuthState {
  status: AuthStatus;
  staffIdx: number;
  pin: string;
  token: string | null;
  accountId: string | null;
  storeId: string | null;
  terminalId: string | null;
  storeInfo: StoreInfo | null;
  assetName: string | null;
  error: string | null;
}

/** The pristine, signed-out state. `logout` resets to exactly this. */
export const guestAuthState: AuthState = {
  status: 'guest',
  staffIdx: 0,
  pin: '',
  token: null,
  accountId: null,
  storeId: null,
  terminalId: null,
  storeInfo: null,
  assetName: null,
  error: null,
};

/**
 * Startup state. Rehydrated from a still-valid session in `sessionStorage`
 * so a browser refresh keeps the user signed in instead of routing to login.
 * An expired/absent/corrupt session falls back to the guest state.
 */
function buildInitialAuthState(): AuthState {
  const session = loadSession();
  if (!session) return guestAuthState;
  return {
    ...guestAuthState,
    status: 'authenticated',
    token: session.token,
    accountId: session.accountId,
    storeId: session.storeId,
    terminalId: session.terminalId,
    storeInfo: session.storeInfo,
    assetName: session.assetName,
  };
}

export const initialAuthState: AuthState = buildInitialAuthState();

export const authReducer = createReducer(
  initialAuthState,

  on(AuthActions.selectStaff, (state, { staffIdx }) => ({
    ...state,
    staffIdx,
    pin: '',
    error: null,
  })),

  on(AuthActions.keyPress, (state, { key }) => {
    if (state.status === 'verifying') return state;
    if (key === 'clear') return { ...state, pin: '', error: null };
    if (key === 'back') return { ...state, pin: state.pin.slice(0, -1), error: null };
    if (state.pin.length >= 4) return state;
    return { ...state, pin: (state.pin + key).slice(0, 4), error: null };
  }),

  on(AuthActions.setPin, (state, { pin }) => ({
    ...state,
    pin: pin.replace(/\D/g, '').slice(0, 4),
    error: null,
  })),

  on(AuthActions.verifyPin, (state) => ({ ...state, status: 'verifying' as const, error: null })),

  on(
    AuthActions.verifyPinSuccess,
    (state, { token, accountId, storeId, terminalId, storeInfo, assetName }) => ({
      ...state,
      status: 'authenticated' as const,
      token,
      accountId,
      storeId,
      terminalId,
      storeInfo,
      assetName,
      pin: '',
      error: null,
    }),
  ),

  on(AuthActions.verifyPinFailure, (state, { error }) => ({
    ...state,
    status: 'guest' as const,
    pin: '',
    error,
  })),

  on(AuthActions.logout, () => guestAuthState),
);
